import { useTheme } from '../contexts/ThemeContext';
import { getComponentColor, getColor as getColorFromPalette } from '../utils/colorPalette';

/**
 * Custom hook for theme-aware styling
 * Uses componentColors.json to map component references to colorPalette values
 */
const useThemeStyles = () => {
  const { currentTheme, isDark, isLight } = useTheme();

  /**
   * Resolve a component color path to an actual color value
   * Handles nested paths and resolves color references from componentColors.json
   */
  const resolveComponentColor = (path) => {
    return getComponentColor(path, currentTheme);
  };

  // Common color combinations - resolved from componentColors.json
  const getColors = () => {
    const backgroundPrimary = resolveComponentColor('background.primary');
    const backgroundSecondary = resolveComponentColor('background.secondary');
    const backgroundSurface = resolveComponentColor('background.surface');

    const textPrimary = resolveComponentColor('text.primary');
    const textSecondary = resolveComponentColor('text.secondary');
    const textMuted = resolveComponentColor('text.muted');

    const iconPrimary = resolveComponentColor('icon.primary');
    const iconSecondary = resolveComponentColor('icon.secondary');

    const borderPrimary = resolveComponentColor('border.primary');
    const borderSecondary = resolveComponentColor('border.secondary');
    const borderSubtle = resolveComponentColor('border.subtle');

    const interactivePrimary = resolveComponentColor('interactive.primary');
    const interactiveHover = resolveComponentColor('interactive.hover');
    const interactiveFocus = resolveComponentColor('interactive.focus');
    const interactiveDisabled = resolveComponentColor('interactive.disabled');

    const semanticSuccess = resolveComponentColor('semantic.success');
    const semanticWarning = resolveComponentColor('semantic.warning');
    const semanticError = resolveComponentColor('semantic.error');
    const semanticInfo = resolveComponentColor('semantic.info');

    const paperBackground = resolveComponentColor('paper.background');
    const paperBackgroundSecondary = resolveComponentColor('paper.backgroundSecondary');
    const paperBorder = resolveComponentColor('paper.border');
    const paperShadow = resolveComponentColor('paper.shadow');
    const paperTextShadow = resolveComponentColor('paper.textShadow');

    const primaryMain = resolveComponentColor('primary.main');
    const primaryContrastText = resolveComponentColor('primary.contrastText');

    const sidebarBackground = resolveComponentColor('sidebar.background');
    const sidebarHeaderBackground = resolveComponentColor('sidebar.headerBackground');
    const sidebarHeaderHover = resolveComponentColor('sidebar.headerHover');

    const tooltipBackground = resolveComponentColor('tooltip.background');
    const tooltipColor = resolveComponentColor('tooltip.color');
    const tooltipBorder = resolveComponentColor('tooltip.border');

    // Handle textShadow specially - it's not a color
    const getTextShadow = () => {
      if (paperTextShadow === 'none') {
        return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.3)' : '1px 1px 2px rgba(0, 0, 0, 0.1)';
      }
      return paperTextShadow;
    };
    const textShadowValue = getTextShadow();

    return {
      background: {
        primary: backgroundPrimary,
        secondary: backgroundSecondary,
        surface: backgroundSurface,
      },
      text: {
        primary: textPrimary,
        secondary: textSecondary,
        muted: textMuted,
      },
      icon: {
        primary: iconPrimary,
        secondary: iconSecondary,
      },
      border: {
        primary: borderPrimary,
        secondary: borderSecondary,
        subtle: borderSubtle,
      },
      interactive: {
        primary: interactivePrimary,
        hover: interactiveHover,
        focus: interactiveFocus,
        disabled: interactiveDisabled,
      },
      semantic: {
        success: semanticSuccess,
        warning: semanticWarning,
        error: semanticError,
        info: semanticInfo,
      },
      paper: {
        background: paperBackground,
        backgroundSecondary: paperBackgroundSecondary,
        border: paperBorder,
        shadow: paperShadow,
        textShadow: textShadowValue,
      },
      primary: {
        main: primaryMain,
        contrastText: primaryContrastText,
      },
      sidebar: {
        background: sidebarBackground,
        headerBackground: sidebarHeaderBackground,
        headerHover: sidebarHeaderHover,
      },
      tooltip: {
        background: tooltipBackground,
        color: tooltipColor,
        border: tooltipBorder,
      },
    };
  };

  // Pre-built style objects for common components
  const getComponentStyles = () => {
    const navbarBg = resolveComponentColor('navbar.backgroundColor');
    const navbarColor = resolveComponentColor('navbar.color');
    const navbarBorderBottom = resolveComponentColor('navbar.borderBottom');

    const buttonPrimaryBg = resolveComponentColor('button.primary.backgroundColor');
    const buttonPrimaryColor = resolveComponentColor('button.primary.color');
    const buttonPrimaryBorder = resolveComponentColor('button.primary.border');
    const buttonHoverBg = resolveComponentColor('button.hover.backgroundColor');
    const buttonHoverBorderColor = resolveComponentColor('button.hover.borderColor');

    const cardBg = resolveComponentColor('card.backgroundColor');
    const cardBorder = resolveComponentColor('card.border');
    const cardColor = resolveComponentColor('card.color');

    const inputBg = resolveComponentColor('input.backgroundColor');
    const inputBorder = resolveComponentColor('input.border');
    const inputColor = resolveComponentColor('input.color');
    const inputFocusBorderColor = resolveComponentColor('input.focus.borderColor');
    const inputFocusBoxShadow = resolveComponentColor('input.focus.boxShadow');

    const paperComponentBg = resolveComponentColor('paperComponent.backgroundColor');
    const paperComponentBorder = resolveComponentColor('paperComponent.border');
    const paperComponentColor = resolveComponentColor('paperComponent.color');
    const paperComponentShadow = resolveComponentColor('paperComponent.shadow');
    const paperComponentShadowHover = resolveComponentColor('paperComponent.shadowHover');

    return {
      navbar: {
        backgroundColor: navbarBg,
        color: navbarColor,
        borderBottom: `1px solid ${navbarBorderBottom}`,
      },
      button: {
        primary: {
          backgroundColor: buttonPrimaryBg,
          color: buttonPrimaryColor,
          border: `1px solid ${buttonPrimaryBorder}`,
        },
        hover: {
          backgroundColor: buttonHoverBg,
          borderColor: buttonHoverBorderColor,
        },
      },
      card: {
        backgroundColor: cardBg,
        border: `1px solid ${cardBorder}`,
        color: cardColor,
      },
      input: {
        backgroundColor: inputBg,
        border: `1px solid ${inputBorder}`,
        color: inputColor,
        focus: {
          borderColor: inputFocusBorderColor,
          boxShadow: inputFocusBoxShadow !== 'none' ? `0 0 0 2px ${inputFocusBoxShadow}` : 'none',
        },
      },
      paper: (() => {
        const getShadow = () => {
          if (paperComponentShadow === 'none') return 'none';
          return isDark ? '1px 1px 3px rgba(0, 0, 0, 0.3)' : '1px 1px 2px rgba(0, 0, 0, 0.1)';
        };

        const getShadowHover = () => {
          if (paperComponentShadowHover === 'none') return 'none';
          return isDark ? '1px 1px 5px rgba(0, 0, 0, 0.4)' : '1px 1px 3px rgba(0, 0, 0, 0.15)';
        };

        return {
          backgroundColor: paperComponentBg,
          border: `1px solid ${paperComponentBorder}`,
          color: paperComponentColor,
          shadow: getShadow(),
          shadowHover: getShadowHover(),
        };
      })(),
    };
  };

  // Utility functions
  const getColor = (colorName, shade = 50) => {
    // For backwards compatibility, allow direct color/shade lookups
    return getColorFromPalette(colorName, shade, currentTheme);
  };

  // eslint-disable-next-line no-unused-vars
  const getContrastColor = (backgroundColor) => {
    // Simple contrast logic - can be enhanced with actual color contrast calculation
    return resolveComponentColor('text.primary');
  };

  const getHoverColor = (baseColor, intensity = 'medium') => {
    // For backwards compatibility
    const intensityMap = {
      light: 20,
      medium: 30,
      strong: 40,
    };
    const adjustment = intensityMap[intensity] || 30;
    return getColorFromPalette(baseColor, adjustment, currentTheme);
  };

  return {
    colors: getColors(),
    components: getComponentStyles(),
    getColor,
    getContrastColor,
    getHoverColor,
    currentTheme,
    isDark,
    isLight,
  };
};

export default useThemeStyles;
